import * as React from "react";
import { Box, Chip, Typography } from "@mui/material";
import type { ChunkMetadata } from "./types";
import { MarkdownRenderer } from "../common/MarkdownRenderer";
import {
  chunkHasDisplayContent,
  getChunkTypeLabel,
  isHtmlTableContent,
  normalizeChunkType,
} from "./chunkUtils";

interface ChunkContentViewProps {
  meta: ChunkMetadata;
  /** Mostra chip tipo e metadati in cima (modale) */
  showMeta?: boolean;
}

const markdownBoxSx = {
  "& p": { margin: 0, mb: 1 },
  "& p:last-child": { mb: 0 },
  "& ul, & ol": { pl: 2.5, my: 1 },
  fontSize: "0.9375rem",
  lineHeight: 1.65,
  color: "text.primary",
};

const tableContainerSx = {
  overflowX: "auto",
  borderRadius: 1.5,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  "& table": {
    width: "100%",
    minWidth: 280,
    borderCollapse: "collapse",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
  },
  "& th, & td": {
    border: "1px solid",
    borderColor: "divider",
    px: 1.25,
    py: 0.75,
    verticalAlign: "top",
    textAlign: "left",
  },
  "& th": {
    bgcolor: "action.hover",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  "& tr:nth-of-type(even) td": {
    bgcolor: "action.hover",
    opacity: 0.45,
  },
};

function ChunkSection({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 2.5, "&:last-child": { mb: 0 } }}>
      {label && (
        <Typography
          variant="overline"
          sx={{
            display: "block",
            mb: 0.75,
            color: "text.secondary",
            letterSpacing: "0.06em",
            fontSize: "0.6875rem",
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function MarkdownBlock({ content }: { content: string }) {
  return (
    <Box sx={markdownBoxSx}>
      <MarkdownRenderer>{content}</MarkdownRenderer>
    </Box>
  );
}

function TableBodyBlock({ content }: { content: string }) {
  if (isHtmlTableContent(content)) {
    return (
      <Box
        sx={tableContainerSx}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  return <MarkdownBlock content={content} />;
}

function TextChunkContent({ meta }: { meta: ChunkMetadata }) {
  const showResume =
    Boolean(meta.resume?.trim()) &&
    meta.resume?.trim() !== meta.text?.trim();

  return (
    <>
      {meta.header?.trim() && (
        <ChunkSection label="Intestazione">
          <MarkdownBlock content={meta.header} />
        </ChunkSection>
      )}
      {showResume && (
        <ChunkSection label="Sintesi">
          <MarkdownBlock content={meta.resume!} />
        </ChunkSection>
      )}
      {meta.text?.trim() && (
        <ChunkSection label={showResume ? "Contenuto" : undefined}>
          <MarkdownBlock content={meta.text} />
        </ChunkSection>
      )}
    </>
  );
}

function TableChunkContent({ meta }: { meta: ChunkMetadata }) {
  return (
    <>
      {meta.table_caption?.trim() && (
        <ChunkSection label="Didascalia">
          <MarkdownBlock content={meta.table_caption} />
        </ChunkSection>
      )}
      {meta.table_body?.trim() && (
        <ChunkSection label="Tabella">
          <TableBodyBlock content={meta.table_body} />
        </ChunkSection>
      )}
      {meta.table_footnote?.trim() && (
        <ChunkSection label="Note">
          <MarkdownBlock content={meta.table_footnote} />
        </ChunkSection>
      )}
    </>
  );
}

function ImageChunkContent({ meta }: { meta: ChunkMetadata }) {
  return (
    <>
      {meta.image_caption?.trim() && (
        <ChunkSection label="Didascalia">
          <MarkdownBlock content={meta.image_caption} />
        </ChunkSection>
      )}
      {meta.image_description?.trim() && (
        <ChunkSection label="Descrizione">
          <MarkdownBlock content={meta.image_description} />
        </ChunkSection>
      )}
      {meta.image_footnote?.trim() && (
        <ChunkSection label="Note">
          <MarkdownBlock content={meta.image_footnote} />
        </ChunkSection>
      )}
    </>
  );
}

function FallbackChunkContent({ meta }: { meta: ChunkMetadata }) {
  const fields: { label: string; value: string | undefined }[] = [
    { label: "Intestazione", value: meta.header },
    { label: "Contenuto", value: meta.text },
    { label: "Sintesi", value: meta.resume },
    { label: "Didascalia tabella", value: meta.table_caption },
    { label: "Tabella", value: meta.table_body },
    { label: "Note tabella", value: meta.table_footnote },
    { label: "Didascalia immagine", value: meta.image_caption },
    { label: "Descrizione immagine", value: meta.image_description },
    { label: "Note immagine", value: meta.image_footnote },
  ];

  const nonEmpty = fields.filter((f) => f.value?.trim());
  if (nonEmpty.length === 0) return null;

  return (
    <>
      {nonEmpty.map(({ label, value }) => (
        <ChunkSection key={label} label={label}>
          {label === "Tabella" && value && isHtmlTableContent(value) ? (
            <TableBodyBlock content={value} />
          ) : (
            <MarkdownBlock content={value!} />
          )}
        </ChunkSection>
      ))}
    </>
  );
}

export const ChunkContentView: React.FC<ChunkContentViewProps> = ({
  meta,
  showMeta = false,
}) => {
  const chunkType = normalizeChunkType(meta.type);
  const hasContent = chunkHasDisplayContent(meta);

  const typeSpecific = (
    <>
      {chunkType === "text" && <TextChunkContent meta={meta} />}
      {chunkType === "table" && <TableChunkContent meta={meta} />}
      {chunkType === "image" && <ImageChunkContent meta={meta} />}
      {chunkType === "other" && <FallbackChunkContent meta={meta} />}
    </>
  );

  const needsFallback =
    hasContent &&
    chunkType !== "other" &&
    ((chunkType === "text" &&
      !meta.header?.trim() &&
      !meta.text?.trim() &&
      !meta.resume?.trim()) ||
      (chunkType === "table" &&
        !meta.table_caption?.trim() &&
        !meta.table_body?.trim() &&
        !meta.table_footnote?.trim()) ||
      (chunkType === "image" &&
        !meta.image_caption?.trim() &&
        !meta.image_description?.trim() &&
        !meta.image_footnote?.trim()));

  return (
    <Box>
      {showMeta && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Chip
            label={getChunkTypeLabel(meta.type)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          {meta.category?.trim() && (
            <Chip
              label={meta.category}
              size="small"
              variant="outlined"
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          )}
          {meta.golden_standard && (
            <Chip
              label="Golden standard"
              size="small"
              sx={{
                bgcolor: "warning.main",
                color: "warning.contrastText",
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      )}

      {typeSpecific}
      {needsFallback && <FallbackChunkContent meta={meta} />}

      {!hasContent && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontStyle: "italic", py: 2 }}
        >
          Nessun contenuto testuale disponibile per questo estratto.
        </Typography>
      )}
    </Box>
  );
};
